if (!customElements.get('dd-benefit-carousel')) {
  customElements.define('dd-benefit-carousel', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.slides = [...this.querySelectorAll('[data-benefit-slide]')];
      this.panes = [...this.querySelectorAll('[data-pane-index]')];
      this.dots = [...this.querySelectorAll('[data-benefit-dot]')];
      this.stage = this.querySelector('[data-benefit-stage]');
      if (!this.slides.length || !this.stage) return;
      this.index = Math.min(this.index || 0, this.slides.length - 1);
      this.loop = this.dataset.loop === 'true';
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.reducedMotion.matches;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-benefit-direction], [data-benefit-dot], [data-benefit-pause]');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-benefit-pause')) {
          this.paused = !this.paused;
          this.schedule();
          this.updatePauseButton();
          return;
        }
        if (button.hasAttribute('data-benefit-dot')) this.go(Number(button.dataset.benefitDot), true);
        else this.go(this.index + Number(button.dataset.benefitDirection), true);
      }, options);
      this.addEventListener('keydown', event => {
        if (event.target.closest('input, select, textarea, video, product-form, a')) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        const change = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
        if (!change) return;
        event.preventDefault();
        this.go(this.index + change, true);
      }, options);
      this.stage.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('button, input, select, textarea, product-form')) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, options);
      this.stage.addEventListener('pointerup', event => {
        const start = this.pointer;
        this.pointer = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        this.suppressClickUntil = Date.now() + 350;
        const rtl = getComputedStyle(this).direction === 'rtl';
        this.go(this.index + (dx < 0 ? 1 : -1) * (rtl ? -1 : 1), true);
      }, options);
      this.stage.addEventListener('pointercancel', () => { this.pointer = null; }, options);
      this.stage.addEventListener('click', event => {
        if (Date.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { ...options, capture: true });
      this.stage.addEventListener('dragstart', event => event.preventDefault(), options);
      this.addEventListener('mouseenter', () => clearTimeout(this.timer), options);
      this.addEventListener('mouseleave', () => this.schedule(), options);
      this.addEventListener('focusin', () => clearTimeout(this.timer), options);
      this.addEventListener('focusout', () => this.schedule(), options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) this.paused = true;
        this.schedule();
        this.updatePauseButton();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest?.('[data-benefit-slide]');
        if (slide && this.contains(slide)) this.go(Number(slide.dataset.benefitSlide), true);
      }, options);
      this.render();
      this.schedule();
      this.updatePauseButton();
    }
    go(next, manual = false) {
      const count = this.slides.length;
      this.index = this.loop ? ((next % count) + count) % count : Math.max(0, Math.min(next, count - 1));
      if (manual) this.paused = true;
      this.render();
      this.schedule();
      this.updatePauseButton();
    }
    render() {
      const count = this.slides.length;
      const previous = (this.index - 1 + count) % count;
      const next = (this.index + 1) % count;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.dataset.slot = active ? 'current' : index === next ? 'next' : index === previous ? 'previous' : 'hidden';
        slide.classList.toggle('swiper-slide-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.panes.forEach(pane => {
        const active = Number(pane.dataset.paneIndex) === this.index;
        pane.classList.toggle('is-active', active);
        pane.inert = !active;
        pane.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach(dot => dot.setAttribute('aria-pressed', String(Number(dot.dataset.benefitDot) === this.index)));
      this.querySelectorAll('[data-benefit-direction]').forEach(button => {
        button.disabled = count < 2 || (!this.loop && (Number(button.dataset.benefitDirection) < 0 ? this.index === 0 : this.index === count - 1));
      });
      const status = this.querySelector('[data-benefit-status]');
      if (status) status.textContent = this.slides[this.index].dataset.title;
    }
    schedule() {
      clearTimeout(this.timer);
      const delay = Number(this.dataset.autoplayMs);
      if (!delay || this.paused || document.hidden || this.slides.length < 2) return;
      this.timer = setTimeout(() => {
        if (!this.matches(':focus-within') && !this.matches(':hover')) this.go(this.index + 1);
        else this.schedule();
      }, delay);
    }
    updatePauseButton() {
      const button = this.querySelector('[data-benefit-pause]');
      if (button) {
        button.textContent = this.paused ? button.dataset.playLabel : button.dataset.pauseLabel;
        button.setAttribute('aria-pressed', String(this.paused));
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.timer);
      this.pointer = null;
    }
  });
}
